// src/pages/PageMenu/SalesMenu.jsx
// قائمة المبيعات — بنفس الترويسة الموحّدة للتقارير، وبطاقات أفقية مع وصف قصير وبحث سريع

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Paper, Typography, ButtonBase, TextField, InputAdornment, Alert, Skeleton } from "@mui/material";

import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ReplayIcon from "@mui/icons-material/Replay";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PeopleIcon from "@mui/icons-material/People";
import BarChartIcon from "@mui/icons-material/BarChart";
import PointOfSaleIcon from "@mui/icons-material/PointOfSale";
import SummarizeIcon from "@mui/icons-material/Summarize";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AssignmentReturnOutlinedIcon from "@mui/icons-material/AssignmentReturnOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import SearchIcon from "@mui/icons-material/Search";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

import axios from "../../axios";
import { useAuth } from "../../context/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";

const BORDER = "#e5e7eb";

// أنواع فواتير المبيعات: بالرقم أولًا ثم بالاسم (احتياطي لبيانات قديمة)
const SALES_TYPES = {
  1:  { key: "salesMenu.salesInvoice", fallback: "فاتورة بيع", names: ["بيع", "فاتورة بيع"], icon: <ReceiptLongIcon />, color: "#4F46E5", desc: "فاتورة مبيعات جديدة", perm: "INVOICE_SALES_CREATE" },
  3:  { key: "salesMenu.salesReturn", fallback: "مرتجع مبيعات", names: ["مرتجع مبيعات", "فاتورة مرتجع مبيعات"], icon: <ReplayIcon />, color: "#E11D48", desc: "إرجاع أصناف من عميل", perm: "INVOICE_SALES_RETURN_CREATE" },
  10: { key: "salesMenu.quotation", fallback: "عرض سعر", names: ["عرض سعر", "فاتورة عرض سعر"], icon: <PriceCheckIcon />, color: "#0891B2", desc: "عرض سعر للعميل بدون أثر مخزني", perm: "INVOICE_QUOTATION_CREATE" },
};
const typeIdOf = (t) => {
  const id = Number(t.invoiceTypeId);
  if (SALES_TYPES[id]) return id;
  const name = (t.name || "").trim();
  return Number(Object.keys(SALES_TYPES).find(k => SALES_TYPES[k].names.includes(name))) || null;
};

// ───────────────────────── البطاقة (مربع: أيقونة + اسم) ─────────────────────────
function MenuCard({ item, onClick }) {
  return (
    <ButtonBase
      onClick={onClick}
      focusRipple
      title={item.desc}
      sx={{
        width: "100%", height: "100%", minHeight: { xs: 100, sm: 120, md: 128 },
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: { xs: .75, sm: 1.1 },
        p: { xs: 1, sm: 1.5 }, textAlign: "center",
        borderRadius: { xs: "16px", sm: "18px" }, bgcolor: "#fff", border: `1px solid ${BORDER}`,
        boxShadow: "0 1px 2px rgba(15,23,42,.04)",
        transition: "border-color .2s, box-shadow .2s, transform .2s",
        "&:hover, &.Mui-focusVisible": {
          borderColor: item.color,
          boxShadow: `0 14px 28px -16px ${item.color}`,
          transform: "translateY(-3px)",
          "& .mc-icon": { bgcolor: item.color, color: "#fff", transform: "scale(1.06)" },
        },
        "&:active": { transform: "translateY(-1px)" },
      }}
    >
      <Box className="mc-icon" sx={{
        width: { xs: 42, sm: 50, md: 54 }, height: { xs: 42, sm: 50, md: 54 }, flexShrink: 0,
        borderRadius: { xs: "14px", sm: "16px" },
        display: "grid", placeItems: "center", bgcolor: `${item.color}14`, color: item.color,
        transition: "all .2s", "& svg": { fontSize: { xs: 21, sm: 25, md: 27 } },
      }}>
        {item.icon}
      </Box>
      <Typography sx={{
        fontWeight: 800, fontSize: { xs: 12, sm: 13.5, md: 14 }, color: "#0f172a", lineHeight: 1.4,
        width: "100%", px: .5,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden",
      }}>
        {item.name}
      </Typography>
    </ButtonBase>
  );
}

// ───────────────────────── القسم ─────────────────────────
function MenuSection({ title, subtitle, icon, color, items, navigate, note }) {
  if (!items.length) return null;
  return (
    <Box component="section" sx={{ mb: { xs: 2.5, md: 3.5 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
        <Box sx={{
          width: 34, height: 34, borderRadius: "10px", display: "grid", placeItems: "center",
          bgcolor: `${color}14`, color, "& svg": { fontSize: 19 },
        }}>{icon}</Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: 15.5, md: 16.5 }, color: "#0f172a", lineHeight: 1.3 }}>
            {title}
            <Typography component="span" sx={{ mr: 1, fontSize: 12.5, fontWeight: 700, color: "#94a3b8" }}>({items.length})</Typography>
          </Typography>
          {subtitle && <Typography sx={{ fontSize: 12.5, color: "#64748b" }}>{subtitle}</Typography>}
        </Box>
        <Box sx={{ flex: 1, height: "1px", bgcolor: BORDER, mr: 1 }} />
      </Box>

      {note}

      <Box sx={{
        display: "grid", gap: { xs: 1, sm: 1.25 },
        gridTemplateColumns: {
          xs: "repeat(3, minmax(0,1fr))", sm: "repeat(4, minmax(0,1fr))", md: "repeat(5, minmax(0,1fr))",
          lg: "repeat(6, minmax(0,1fr))", xl: "repeat(7, minmax(0,1fr))",
        },
      }}>
        {items.map(item => <MenuCard key={item.link} item={item} onClick={() => navigate(item.link)} />)}
      </Box>
    </Box>
  );
}

// ======================================================================
export default function SalesMenu() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const isRTL = i18n.language !== "en";
  const companyId = parseInt(localStorage.getItem("companyId"), 10) || null;

  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [loading, setLoading] = useState(!!companyId);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!companyId) { setLoadError(t("salesMenu.companyIdNotFound", { defaultValue: "لم يتم تحديد الشركة" })); return; }
    let alive = true;
    axios.get("/InvoiceTypes", { params: { companyId } })
      .then(res => alive && setInvoiceTypes(Array.isArray(res.data) ? res.data : []))
      .catch(err => {
        console.error("❌ فشل في تحميل أنواع الفواتير:", err);
        if (alive) setLoadError(t("salesMenu.failedToLoadInvoiceTypes", { defaultValue: "تعذّر تحميل أنواع الفواتير" }));
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [companyId, t]);

  const can = (key) => !!PERMISSIONS[key] && hasPermission(PERMISSIONS[key]);
  // صلاحية الأرباح: REPORT_PROFIT إن وُجدت في الثوابت، وإلا صلاحية التقارير العامة
  const canProfit = PERMISSIONS.REPORT_PROFIT ? hasPermission(PERMISSIONS.REPORT_PROFIT) : can("REPORT_VIEW");
  const tr = (key, fallback) => t(key, { defaultValue: fallback });

  // ── العمليات ──
  const operations = useMemo(() => {
    const seen = new Set();
    const typeCards = invoiceTypes
      .map(type => ({ type, id: typeIdOf(type) }))
      .filter(({ id }) => id && !seen.has(id) && seen.add(id) && can(SALES_TYPES[id].perm))
      .sort((a, b) => a.id - b.id)
      .map(({ type, id }) => {
        const c = SALES_TYPES[id];
        return { name: tr(c.key, c.fallback), desc: c.desc, icon: c.icon, color: c.color, link: `/invoices/new/${type.invoiceTypeId}` };
      });

    return [
      ...typeCards,
      can("INVOICE_SALES_CREATE") && { name: tr("salesMenu.pos", "نقطة بيع"), desc: "بيع سريع من شاشة الكاشير", icon: <PointOfSaleIcon />, color: "#7C3AED", link: "/pos" },
      can("BOND_RECEIPT_CREATE") && { name: "سند قبض عميل", desc: "تحصيل دفعة من عميل", icon: <RequestQuoteIcon />, color: "#0D9488", link: "/vouchers/customer-receipt" },
      can("CUSTOMER_CREATE") && { name: tr("salesMenu.addCustomer", "إضافة عميل"), desc: "عميل جديد مع العنوان الوطني", icon: <PersonAddIcon />, color: "#059669", link: "/customers/new" },
      can("CUSTOMER_VIEW") && { name: tr("salesMenu.customersList", "قائمة العملاء"), desc: "بحث وتعديل بيانات العملاء", icon: <PeopleIcon />, color: "#047857", link: "/customers" },
    ].filter(Boolean);
  }, [invoiceTypes, hasPermission, t]);

  // ── التقارير ──
  const reports = [
    can("INVOICE_SALES_VIEW") && { name: tr("salesMenu.allSalesInvoices", "فواتير المبيعات"), desc: "كل فواتير البيع مع البحث والطباعة", icon: <ListAltIcon />, color: "#2563EB", link: "/reports/all-invoices?direction=2&invoiceTypeId=1" },
    can("INVOICE_SALES_RETURN_VIEW") && { name: tr("salesMenu.allSalesReturns", "مرتجعات المبيعات"), desc: "كل فواتير المرتجع", icon: <AssignmentReturnOutlinedIcon />, color: "#E11D48", link: "/reports/all-invoices?direction=2&invoiceTypeId=3" },
    can("INVOICE_QUOTATION_VIEW") && { name: tr("salesMenu.allQuotations", "عروض الأسعار"), desc: "كل عروض الأسعار المحفوظة", icon: <DescriptionOutlinedIcon />, color: "#0891B2", link: "/reports/all-invoices?direction=0&invoiceTypeId=10" },
    can("REPORT_VIEW") && { name: tr("salesMenu.dailySalesReport", "المبيعات حسب اليوم"), desc: "إجمالي المبيعات والمرتجعات لكل يوم", icon: <CalendarTodayIcon />, color: "#D97706", link: "/reports/aggregated-sales-ByDay" },
    can("REPORT_VIEW") && { name: tr("salesMenu.aggregatedItemsSalesReport", "مبيعات الأصناف — تجميعي"), desc: "الكميات والمبالغ لكل صنف", icon: <SummarizeIcon />, color: "#0D9488", link: "/reports/aggregated-sales-items" },
    can("REPORT_VIEW") && { name: tr("salesMenu.detailedItemsSalesReport", "مبيعات الأصناف — تفصيلي"), desc: "كل صنف في كل فاتورة", icon: <BarChartIcon />, color: "#10B981", link: "/reports/sales-items-detail" },
    can("REPORT_VIEW") && { name: tr("salesMenu.financialTransactions", "الحركات المالية"), desc: "حركات الخزينة وحسابات العملاء", icon: <AccountBalanceWalletOutlinedIcon />, color: "#B45309", link: "/LedgerReport/ledgers" },
    can("REPORT_VIEW") && { name: "تقرير الفوترة الإلكترونية", desc: "حالة إرسال الفواتير لهيئة الزكاة", icon: <FactCheckIcon />, color: "#4F46E5", link: "/reports/zatca" },
  ].filter(Boolean);

  // ── الأرباح ──
  const profits = [
    canProfit && { name: "أرباح الفواتير", desc: "الإيراد والتكلفة والربح لكل فاتورة", icon: <SavingsOutlinedIcon />, color: "#059669", link: "/reports/invoice-profit" },
    canProfit && { name: "أرباح الأصناف — تجميعي", desc: "ربح وهامش كل صنف خلال الفترة", icon: <Inventory2OutlinedIcon />, color: "#16A34A", link: "/reports/items-profit-summary" },
    canProfit && { name: "أرباح الأصناف — تفصيلي", desc: "ربح كل صنف في كل فاتورة ومصدر تكلفته", icon: <QueryStatsIcon />, color: "#15803D", link: "/reports/items-profit-detail" },
  ].filter(Boolean);

  // ── البحث السريع ──
  const q = search.trim().toLowerCase();
  const match = (list) => (q ? list.filter(i => `${i.name} ${i.desc || ""}`.toLowerCase().includes(q)) : list);
  const ops = match(operations), reps = match(reports), profs = match(profits);
  const nothing = q && !ops.length && !reps.length && !profs.length;

  return (
    <Box dir={isRTL ? "rtl" : "ltr"} sx={{ px: { xs: 1, sm: 2, md: 3 }, py: { xs: 1, md: 2 } }}>
      {/* الترويسة — نفس ترويسة التقارير */}
      <Paper elevation={0} sx={{
        position: "relative", overflow: "hidden", mb: 2, borderRadius: "16px", color: "#fff",
        px: { xs: 1.75, sm: 2.5 }, py: { xs: 1.75, sm: 2.25 },
        background: "linear-gradient(120deg, #4338ca 0%, #4f46e5 45%, #6366f1 100%)",
        boxShadow: "0 18px 40px -22px rgba(67,56,202,.75)",
      }}>
        <Box aria-hidden sx={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: "radial-gradient(420px 220px at 12% -40%, rgba(255,255,255,.22), transparent 70%), radial-gradient(360px 200px at 100% 140%, rgba(16,185,129,.35), transparent 70%)",
        }} />
        <Box sx={{ position: "relative", display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 }, minWidth: 0 }}>
            <Box sx={{
              width: { xs: 44, sm: 50 }, height: { xs: 44, sm: 50 }, flexShrink: 0, borderRadius: "13px",
              display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,.16)", border: "1px solid rgba(255,255,255,.28)",
              "& svg": { fontSize: { xs: 23, sm: 26 } },
            }}>
              <StorefrontOutlinedIcon />
            </Box>
            <Box>
              <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: "1.2rem", sm: "1.45rem", md: "1.6rem" }, lineHeight: 1.3 }}>
                {tr("salesMenu.dashboardTitle", "المبيعات")}
              </Typography>
            </Box>
          </Box>

          <TextField
            size="small" value={search} onChange={e => setSearch(e.target.value)} placeholder="ابحث عن شاشة أو تقرير..."
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "rgba(255,255,255,.8)" }} /></InputAdornment> }}
            sx={{
              width: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": {
                borderRadius: "11px", color: "#fff", bgcolor: "rgba(255,255,255,.14)",
                "& fieldset": { borderColor: "rgba(255,255,255,.3)" },
                "&:hover fieldset": { borderColor: "rgba(255,255,255,.6)" },
                "&.Mui-focused fieldset": { borderColor: "#fff" },
              },
              "& input::placeholder": { color: "rgba(255,255,255,.75)", opacity: 1 },
            }}
          />
        </Box>
      </Paper>

      {loadError && (
        <Alert severity="warning" sx={{ mb: 2, borderRadius: "12px" }}>
          {loadError} — تظهر بقية الشاشات بشكل طبيعي.
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "repeat(3,1fr)", sm: "repeat(4,1fr)", md: "repeat(5,1fr)", lg: "repeat(6,1fr)" }, mb: 3 }}>
          {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={120} sx={{ borderRadius: "14px" }} />)}
        </Box>
      ) : (
        <MenuSection
          title={tr("العمليات")}

          icon={<BoltOutlinedIcon />} color="#4F46E5" items={ops} navigate={navigate}
        />
      )}

      <MenuSection
        title={tr("تقارير المبيعات")}
        icon={<InsightsOutlinedIcon />} color="#0891B2" items={reps} navigate={navigate}
      />

      <MenuSection
        title="الأرباح"
      
        icon={<SavingsOutlinedIcon />} color="#059669" items={profs} navigate={navigate}
        note={
          <Box sx={{ display: "flex", alignItems: "center", gap: .75, mb: 1.25, fontSize: 12.5, color: "#64748b" }}>
          </Box>
        }
      />

      {nothing && (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", borderRadius: "14px", border: `1px dashed ${BORDER}`, color: "#64748b" }}>
          <SearchIcon sx={{ fontSize: 36, color: "#cbd5e1" }} />
          <Typography sx={{ fontWeight: 700, mt: .5 }}>لا توجد شاشة باسم "{search}"</Typography>
        </Paper>
      )}

    </Box>
  );
}